import { IDatabaseColumnModel } from "@kuika/kuika-cl-model";

// Audit / soft-delete metadata columns. These end up at the very end
// of the grid (and every export) so the user sees real business data
// first. Match is case-insensitive on the column name — Kuika tables
// in the wild use both `CreatedAt`/`UpdatedAt` and the older
// `CreateAt`/`UpdateAt` shape, so both are recognized.
//
// `toLowerCase()` (not `toLocaleLowerCase`) is intentional: ASCII
// identifiers, no Turkish-i trap.
const AUDIT_COLUMN_NAMES_LOWER: ReadonlySet<string> = new Set<string>([
  "isdeleted",
  "createdat",
  "updatedat",
  "createat",
  "updateat"
]);

const isAuditColumn = (column: IDatabaseColumnModel): boolean => {
  const name: string = column.name ?? "";
  if (name.length === 0) {
    return false;
  }
  return AUDIT_COLUMN_NAMES_LOWER.has(name.toLowerCase());
};

// KOA-5857 — DataGrip-style column ordering.
// Primary keys first (in their declared PK order), then required (NOT NULL)
// columns, then nullable columns, then audit / soft-delete metadata
// (IsDeleted, CreatedAt, UpdatedAt, CreateAt, UpdateAt — case-insensitive)
// pushed to the very end regardless of nullability. Declared order
// preserved inside each group. Used by both the grid render and the
// export pipeline so what the user sees on screen is what they get
// in CSV / XLSX / SQL output.
//
// Primary keys still come first even if a column happens to be named
// like an audit column — `IsDeleted` declared as PK keeps its leading
// slot. In practice none of the audit names are ever PKs, but the
// PK-first guarantee is load-bearing for the row editor's update path.
export const orderColumnsLikeDataGrip = (
  columns: IDatabaseColumnModel[],
  primaryKeyColumns: string[]
): IDatabaseColumnModel[] => {
  if (columns.length === 0) {
    return columns;
  }
  const pkSet: Set<string> = new Set(primaryKeyColumns);
  const pkFirst: IDatabaseColumnModel[] = primaryKeyColumns
    .map((name: string): IDatabaseColumnModel | undefined =>
      columns.find((c: IDatabaseColumnModel): boolean => c.name === name)
    )
    .filter((c: IDatabaseColumnModel | undefined): c is IDatabaseColumnModel => c !== undefined);
  const rest: IDatabaseColumnModel[] = columns.filter((c: IDatabaseColumnModel): boolean => !pkSet.has(c.name));
  const audit: IDatabaseColumnModel[] = rest.filter(isAuditColumn);
  const nonAudit: IDatabaseColumnModel[] = rest.filter((c: IDatabaseColumnModel): boolean => !isAuditColumn(c));
  const required: IDatabaseColumnModel[] = nonAudit.filter((c: IDatabaseColumnModel): boolean => c.nullable === false);
  const nullable: IDatabaseColumnModel[] = nonAudit.filter((c: IDatabaseColumnModel): boolean => c.nullable !== false);
  return [...pkFirst, ...required, ...nullable, ...audit];
};
